import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./App.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AdminPage, {
  AdminCAProfilesPage,
  AdminCallRequestsPage,
  AdminContactsPage,
  AdminCreateCustomerPage,
  AdminCustomerLogDetailPage,
  AdminCustomerLogsPage,
  AdminAiReportPage,
  AdminDashboard,
  AdminFunnelPage,
  AdminLogsPage,
  AdminMembersDirectoryPage,
  AdminOrderDetailPage,
  AdminOrdersPage,
  AdminInvoicesPage,
  AdminPaymentsPage,
  AdminProfilePage,
  AdminStalledUsersPage,
  AdminTeamManagementPage,
  AdminUserDetailPage,
  AdminUsersPage,
} from "./pages/AdminPage";
import SignInPage from "./pages/SignInPage";

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#F5F4FB] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Redirects already-authenticated users away from the sign-in page
function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user && user !== false) return <Navigate to="/admin" replace />;
  return children;
}

// Requires any valid admin panel login (ca, admin, super_admin)
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || user === false) return <Navigate to="/sign-in" replace />;
  return children;
}

// Requires super_admin role only — redirects others to /admin
function SuperAdminRoute({ children }) {
  const { user, loading, isSuperAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || user === false) return <Navigate to="/sign-in" replace />;
  if (!isSuperAdmin) return <Navigate to="/admin" replace />;
  return children;
}

// Requires admin or super_admin role — redirects CAs to /admin/orders
function AdminOnlyRoute({ children }) {
  const { user, loading, isAdminOrAbove } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || user === false) return <Navigate to="/sign-in" replace />;
  if (!isAdminOrAbove) return <Navigate to="/admin/orders" replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/admin" replace />} />
      <Route
        path="/sign-in"
        element={
          <AuthRoute>
            <SignInPage />
          </AuthRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      >
        {/* Dashboard — admin & super_admin only; CA redirected to orders */}
        <Route
          index
          element={
            <AdminOnlyRoute>
              <AdminDashboard />
            </AdminOnlyRoute>
          }
        />

        {/* Orders — all roles (CA sees only their assigned orders) */}
        <Route path="orders" element={<AdminOrdersPage />} />
        <Route path="orders/:orderId" element={<AdminOrderDetailPage />} />

        {/* Call Requests — all roles (CA sees only their own) */}
        <Route path="call-requests" element={<AdminCallRequestsPage />} />

        {/* My Profile — all roles (shows the signed-in user's own profile) */}
        <Route path="profile" element={<AdminProfilePage />} />

        {/* Users — admin & super_admin only */}
        <Route
          path="users"
          element={
            <AdminOnlyRoute>
              <AdminUsersPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="users/:userId"
          element={
            <AdminOnlyRoute>
              <AdminUserDetailPage />
            </AdminOnlyRoute>
          }
        />

        {/* Add Customer — admin & super_admin only */}
        <Route
          path="create-customer"
          element={
            <AdminOnlyRoute>
              <AdminCreateCustomerPage />
            </AdminOnlyRoute>
          }
        />

        {/* CA Profiles — admin & super_admin only */}
        <Route
          path="ca-profiles"
          element={
            <AdminOnlyRoute>
              <AdminCAProfilesPage />
            </AdminOnlyRoute>
          }
        />

        {/* Members Directory — admin & super_admin only */}
        <Route
          path="members"
          element={
            <AdminOnlyRoute>
              <AdminMembersDirectoryPage />
            </AdminOnlyRoute>
          }
        />

        {/* Contacts — admin & super_admin only */}
        <Route
          path="contacts"
          element={
            <AdminOnlyRoute>
              <AdminContactsPage />
            </AdminOnlyRoute>
          }
        />

        {/* Visitor Funnel — admin & super_admin only */}
        <Route
          path="funnel"
          element={
            <AdminOnlyRoute>
              <AdminFunnelPage />
            </AdminOnlyRoute>
          }
        />

        {/* Stalled Users — super_admin only */}
        <Route
          path="stalled-users"
          element={
            <SuperAdminRoute>
              <AdminStalledUsersPage />
            </SuperAdminRoute>
          }
        />

        {/* Payments & Sales Analytics — super_admin only */}
        <Route
          path="payments"
          element={
            <SuperAdminRoute>
              <AdminPaymentsPage />
            </SuperAdminRoute>
          }
        />

        {/* Invoices — super_admin only */}
        <Route
          path="invoices"
          element={
            <SuperAdminRoute>
              <AdminInvoicesPage />
            </SuperAdminRoute>
          }
        />

        {/* Activity Logs — super_admin only */}
        <Route
          path="logs"
          element={
            <SuperAdminRoute>
              <AdminLogsPage />
            </SuperAdminRoute>
          }
        />

        {/* Customer Logs (per-customer activity) — super_admin only */}
        <Route
          path="customer-logs"
          element={
            <SuperAdminRoute>
              <AdminCustomerLogsPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="customer-logs/:userId"
          element={
            <SuperAdminRoute>
              <AdminCustomerLogDetailPage />
            </SuperAdminRoute>
          }
        />

        {/* AI Performance Reports — super_admin only */}
        <Route
          path="ai-reports"
          element={
            <SuperAdminRoute>
              <AdminAiReportPage />
            </SuperAdminRoute>
          }
        />

        {/* Team Management — super_admin only */}
        <Route
          path="team"
          element={
            <SuperAdminRoute>
              <AdminTeamManagementPage />
            </SuperAdminRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
